import {
  addCurrentAuthHeader,
  type SessionService,
} from '@efdigital/ll-addon-auth';
import type {
  AdminPostAbandonRequestBody,
  AdminPostAbandonResponseBody,
  AdminPostQuotaGrantRequestBody,
  AdminPostQuotaGrantResponseBody,
  AdminPostResolveRequestBody,
  AdminPostResolveResponseBody,
  CoachingPlanQuotaSummary,
  MessageResponse,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';

const SESSIONS_API_PREFIX = '/ll-coaching-sessions/admin';

/**
 * `@efdigital/ll-cdk-coaching-sessions-api-types` doesn't publish these yet —
 * the admin cancel endpoint hasn't merged/released on the CDK side. Typed
 * locally to match its documented contract; swap for the real import once
 * a release picks it up.
 */
export interface AdminCancelSessionRequestBody {
  cancelReason: string;
}

/**
 * `@efdigital/ll-cdk-coaching-sessions-api-types` doesn't publish these yet —
 * the admin reschedule endpoint hasn't merged/released on the CDK side. Typed
 * locally to match its documented contract; swap for the real import once
 * a release picks it up.
 */
export interface AdminRescheduleSessionRequestBody {
  startTime: string;
  endTime: string;
}

/**
 * `@efdigital/ll-cdk-coaching-sessions-api-types` doesn't publish these yet —
 * the admin reassign-coach endpoint hasn't merged/released on the CDK side.
 * Typed locally to match its documented contract; swap for the real import once
 * a release picks it up.
 */
export interface AdminReassignCoachRequestBody {
  /** Omit to unassign the current coach without naming a replacement. */
  newCoachUserId?: string;
  reason: string;
}

/** One end of a reassignment, as returned by the admin reassign-coach endpoint. */
export interface ReassignedPartnership {
  partnershipId: string;
  coachUserId: string;
}

/** See {@link AdminReassignCoachRequestBody} for why this is typed locally. */
export interface AdminReassignCoachResponseBody {
  coachingPlanId: string;
  outgoing: ReassignedPartnership | null;
  incoming: ReassignedPartnership | null;
  chemistryQuotaGranted: number;
  cancelledSessionIds: string[];
  abandonedSessionIds: string[];
}

export class AdminSessionsRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AdminSessionsRequestError';
    this.status = status;
  }
}

export async function parseAdminSessionsErrorResponse(
  response: Response
): Promise<never> {
  let message = response.statusText || 'Request failed';

  try {
    const body = (await response.json()) as MessageResponse;
    if (body.message) {
      message = body.message;
    }
  } catch {
    // Use statusText when body is not JSON.
  }

  throw new AdminSessionsRequestError(message, response.status);
}

export async function buildAdminResolveSessionRequest(
  session: SessionService,
  sessionId: string,
  body: AdminPostResolveRequestBody
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${session.apis.systemsApi.host}${SESSIONS_API_PREFIX}/coaching-sessions/${encodeURIComponent(sessionId)}/resolve`,
    init
  );
}

export async function parseAdminResolveSessionResponse(
  response: Response
): Promise<AdminPostResolveResponseBody> {
  if (!response.ok) {
    return parseAdminSessionsErrorResponse(response);
  }

  return (await response.json()) as AdminPostResolveResponseBody;
}

export async function buildAdminAbandonSessionRequest(
  session: SessionService,
  sessionId: string,
  body: AdminPostAbandonRequestBody = {}
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${session.apis.systemsApi.host}${SESSIONS_API_PREFIX}/coaching-sessions/${encodeURIComponent(sessionId)}/abandon`,
    init
  );
}

export async function buildAdminCancelSessionRequest(
  session: SessionService,
  sessionId: string,
  body: AdminCancelSessionRequestBody
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${session.apis.systemsApi.host}${SESSIONS_API_PREFIX}/coaching-sessions/${encodeURIComponent(sessionId)}/cancel`,
    init
  );
}

export async function parseAdminCancelSessionResponse(
  response: Response
): Promise<void> {
  if (!response.ok) {
    return parseAdminSessionsErrorResponse(response);
  }
}

export async function buildAdminRescheduleSessionRequest(
  session: SessionService,
  sessionId: string,
  body: AdminRescheduleSessionRequestBody
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${session.apis.systemsApi.host}${SESSIONS_API_PREFIX}/coaching-sessions/${encodeURIComponent(sessionId)}/reschedule`,
    init
  );
}

export async function parseAdminRescheduleSessionResponse(
  response: Response
): Promise<void> {
  if (!response.ok) {
    return parseAdminSessionsErrorResponse(response);
  }
}

export async function buildAdminClearExternalReviewRequest(
  session: SessionService,
  sessionId: string
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    { session }
  );

  return new Request(
    `${session.apis.systemsApi.host}${SESSIONS_API_PREFIX}/coaching-sessions/${encodeURIComponent(sessionId)}/clear-external-review`,
    init
  );
}

export async function parseAdminClearExternalReviewResponse(
  response: Response
): Promise<void> {
  if (!response.ok) {
    return parseAdminSessionsErrorResponse(response);
  }
}

export async function parseAdminAbandonSessionResponse(
  response: Response
): Promise<AdminPostAbandonResponseBody> {
  if (!response.ok) {
    return parseAdminSessionsErrorResponse(response);
  }

  return (await response.json()) as AdminPostAbandonResponseBody;
}

export async function buildAdminReassignCoachRequest(
  session: SessionService,
  planId: string,
  body: AdminReassignCoachRequestBody
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${session.apis.systemsApi.host}${SESSIONS_API_PREFIX}/coaching-plans/${encodeURIComponent(planId)}/reassign-coach`,
    init
  );
}

export async function parseAdminReassignCoachResponse(
  response: Response
): Promise<AdminReassignCoachResponseBody> {
  if (!response.ok) {
    return parseAdminSessionsErrorResponse(response);
  }

  return (await response.json()) as AdminReassignCoachResponseBody;
}

export async function buildAdminQuotaGrantRequest(
  session: SessionService,
  planId: string,
  idempotencyKey: string,
  body: AdminPostQuotaGrantRequestBody
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${session.apis.systemsApi.host}${SESSIONS_API_PREFIX}/coaching-plans/${encodeURIComponent(planId)}/quota-grant`,
    init
  );
}

export async function parseAdminQuotaGrantResponse(
  response: Response
): Promise<AdminPostQuotaGrantResponseBody> {
  if (!response.ok) {
    return parseAdminSessionsErrorResponse(response);
  }

  return (await response.json()) as AdminPostQuotaGrantResponseBody;
}

export async function buildAdminGetQuotaRequest(
  session: SessionService,
  planId: string
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    { session }
  );

  return new Request(
    `${session.apis.systemsApi.host}${SESSIONS_API_PREFIX}/coaching-plans/${encodeURIComponent(planId)}/quota`,
    init
  );
}

export async function parseAdminGetQuotaResponse(
  response: Response
): Promise<CoachingPlanQuotaSummary> {
  if (!response.ok) {
    return parseAdminSessionsErrorResponse(response);
  }

  return (await response.json()) as CoachingPlanQuotaSummary;
}
